import { useState } from 'react'
import { router } from '@inertiajs/react'
import { PublicLayout } from '@/components/layout/PublicLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn, formatDate } from '@/lib/utils'
import { Stamp, CalendarDays, ArrowRight } from 'lucide-react'

type Props = {
  states: { id: string; code: string; name: string }[]
}

export default function NotaryRegistrationNew({ states }: Props) {
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [commissionNumber, setCommissionNumber] = useState('')
  const [commissionState, setCommissionState] = useState('')
  const [commissionExpiry, setCommissionExpiry] = useState<Date | undefined>(undefined)
  const [bio, setBio] = useState('')
  const [sessionModes, setSessionModes] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const toggleSessionMode = (mode: string) => {
    setSessionModes(prev =>
      prev.includes(mode) ? prev.filter(m => m !== mode) : [...prev, mode]
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: Record<string, string> = {}
    if (!fullName.trim()) newErrors.full_name = 'Full name is required'
    if (!email.trim()) newErrors.email = 'Email is required'
    if (!commissionNumber.trim()) newErrors.commission_number = 'Commission number is required'
    if (!commissionState) newErrors.commission_state = 'Commission state is required'
    if (!commissionExpiry) newErrors.commission_expiry = 'Commission expiry is required'
    if (sessionModes.length === 0) newErrors.session_modes = 'Select at least one session mode'

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setIsSubmitting(true)
    setErrors({})
    router.post('/notary_registration', {
      notary_registration: {
        full_name: fullName.trim(),
        email_address: email.trim(),
        phone: phone.trim() || null,
        commission_number: commissionNumber.trim(),
        commission_state: commissionState,
        commission_expiry: commissionExpiry?.toISOString().split('T')[0],
        bio: bio.trim() || null,
        session_modes: sessionModes,
      }
    }, {
      onFinish: () => setIsSubmitting(false),
      onError: (errs) => setErrors(errs as Record<string, string>),
    })
  }

  return (
    <PublicLayout title="Notary Registration">
      <div className="max-w-lg mx-auto pb-16">
        <div className="mb-8 text-center">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Stamp className="w-6 h-6 text-muted-foreground" />
          </div>
          <h1 className="text-xl font-semibold tracking-tight">Become a Notary</h1>
          <p className="text-sm text-muted-foreground mt-1">Register as a notary public to provide notarization services</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <section>
            <h2 className="text-sm font-medium text-foreground mb-3">Personal Information</h2>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Full Name *</Label>
                <Input
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  placeholder="John Doe"
                  className={cn(errors.full_name && 'border-destructive')}
                />
                {errors.full_name && <p className="text-xs text-destructive">{errors.full_name}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Email Address *</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="john@example.com"
                    className={cn(errors.email && 'border-destructive')}
                  />
                  {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Phone Number</Label>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Commission Details */}
          <section>
            <h2 className="text-sm font-medium text-foreground mb-3">Commission Details</h2>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Commission Number *</Label>
                <Input
                  value={commissionNumber}
                  onChange={e => setCommissionNumber(e.target.value)}
                  placeholder="e.g. 12345678"
                  className={cn(errors.commission_number && 'border-destructive')}
                />
                {errors.commission_number && <p className="text-xs text-destructive">{errors.commission_number}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Commission State *</Label>
                  <select
                    value={commissionState}
                    onChange={e => setCommissionState(e.target.value)}
                    className={cn(
                      'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                      errors.commission_state && 'border-destructive'
                    )}
                  >
                    <option value="">Select state...</option>
                    {states.map(s => (
                      <option key={s.id} value={s.code}>{s.name}</option>
                    ))}
                  </select>
                  {errors.commission_state && <p className="text-xs text-destructive">{errors.commission_state}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Commission Expiry *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal h-9 text-sm',
                          !commissionExpiry && 'text-muted-foreground',
                          errors.commission_expiry && 'border-destructive'
                        )}
                      >
                        <CalendarDays className="mr-2 h-3.5 w-3.5" />
                        {commissionExpiry ? formatDate(commissionExpiry.toISOString()) : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={commissionExpiry}
                        onSelect={setCommissionExpiry}
                        disabled={(date) => date < new Date()}
                      />
                    </PopoverContent>
                  </Popover>
                  {errors.commission_expiry && <p className="text-xs text-destructive">{errors.commission_expiry}</p>}
                </div>
              </div>
            </div>
          </section>

          {/* Bio */}
          <section>
            <h2 className="text-sm font-medium text-foreground mb-3">About You</h2>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Bio</Label>
              <Textarea
                value={bio}
                onChange={e => setBio(e.target.value)}
                placeholder="Tell us about your experience and specializations..."
                rows={4}
              />
            </div>
          </section>

          {/* Session Modes */}
          <section>
            <h2 className="text-sm font-medium text-foreground mb-3">Session Modes *</h2>
            <p className="text-xs text-muted-foreground mb-3">Select the session modes you can provide</p>
            <div className="space-y-2">
              <label className={cn(
                'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all',
                sessionModes.includes('in_person') ? 'border-foreground bg-muted' : 'border-border hover:border-border'
              )}>
                <Checkbox
                  checked={sessionModes.includes('in_person')}
                  onCheckedChange={() => toggleSessionMode('in_person')}
                />
                <div>
                  <span className="text-sm font-medium text-foreground">In Person</span>
                  <p className="text-xs text-muted-foreground">Meet signers at a physical location</p>
                </div>
              </label>
              <label className={cn(
                'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all',
                sessionModes.includes('ron') ? 'border-foreground bg-muted' : 'border-border hover:border-border'
              )}>
                <Checkbox
                  checked={sessionModes.includes('ron')}
                  onCheckedChange={() => toggleSessionMode('ron')}
                />
                <div>
                  <span className="text-sm font-medium text-foreground">Remote Online Notarization (RON)</span>
                  <p className="text-xs text-muted-foreground">Conduct sessions via secure video call</p>
                </div>
              </label>
            </div>
            {errors.session_modes && <p className="text-xs text-destructive mt-1">{errors.session_modes}</p>}
          </section>

          {/* Submit */}
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit Registration'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </form>
      </div>
    </PublicLayout>
  )
}
